"use client";

import { useState, useEffect, useCallback, useRef } from "react";

type Phase = "entering" | "holding" | "exiting" | "done";

export interface SectionInterstitialProps {
  sectionIndex: number;
  sectionLabel: string;
  totalSections: number;
  explain: string;
  tip: string;
  /**
   * Suspend le décompte tant que c'est vrai, sans le remettre à zéro.
   *
   * Le studio y branche la pédale forte : la lecture d'un conseil ne se règle
   * pas à la vitesse moyenne de 200 mots/minute que suppose `getHoldDuration`,
   * et la seule commande que le joueur ait sous la main sans lâcher le clavier
   * est la pédale.
   */
  holdPaused?: boolean;
  onComplete: () => void;
}

function getHoldDuration(explain: string, tip: string): number {
  const wordCount = (explain + " " + tip).split(/\s+/).length;
  const readingTimeMs = (wordCount / 200) * 60 * 1000;
  return Math.min(12000, Math.max(3500, readingTimeMs + 1500));
}

const stagger = (delayMs: number): React.CSSProperties => ({
  animation: `si-stagger 400ms cubic-bezier(0.16, 1, 0.3, 1) ${delayMs}ms both`,
});

export function SectionInterstitial({
  sectionIndex,
  sectionLabel,
  totalSections,
  explain,
  tip,
  holdPaused = false,
  onComplete,
}: SectionInterstitialProps) {
  const [phase, setPhase] = useState<Phase>("entering");
  const [hovered, setHovered] = useState(false);
  // Là où le décompte s'est arrêté, en pourcentage. La barre le porte pendant
  // l'attente : sans lui elle repartait de zéro à chaque pause, ce qui se lit
  // comme une remise à zéro du minuteur — l'inverse de ce qui se passe.
  const [frozenPct, setFrozenPct] = useState(0);

  // La pédale et le survol suspendent la même chose, pour la même raison :
  // quelqu'un est en train de lire.
  const paused = hovered || holdPaused;

  const holdDuration = getHoldDuration(explain, tip);

  const holdStartRef = useRef(0);
  const holdRemainingRef = useRef(holdDuration);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // entering → holding
  useEffect(() => {
    if (phase !== "entering") return;
    const timer = setTimeout(() => setPhase("holding"), 500);
    return () => clearTimeout(timer);
  }, [phase]);

  // holding (suspendu par le survol ou la pédale)
  useEffect(() => {
    if (phase !== "holding") return;

    if (paused) {
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }

      // `holdStartRef` à zéro veut dire qu'aucun minuteur ne courait — pédale
      // déjà enfoncée quand le rideau s'ouvre, ou effet rejoué alors qu'on
      // était déjà en attente. Sans cette garde, l'écart se compte depuis
      // l'époque Unix et le décompte est consommé d'un coup : le conseil
      // disparaît à la seconde où on relâche.
      const elapsed =
        holdStartRef.current === 0 ? 0 : Date.now() - holdStartRef.current;
      const remaining = Math.max(0, holdRemainingRef.current - elapsed);

      holdStartRef.current = 0;
      holdRemainingRef.current = remaining;
      setFrozenPct(((holdDuration - remaining) / holdDuration) * 100);

      return;
    }

    holdStartRef.current = Date.now();
    holdTimerRef.current = setTimeout(() => setPhase("exiting"), holdRemainingRef.current);
    return () => {
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    };
  }, [phase, paused, holdDuration]);

  useEffect(() => {
    if (phase === "holding") {
      holdRemainingRef.current = holdDuration;
      setFrozenPct(0);
    }
  }, [phase, holdDuration]);

  // exiting → done
  useEffect(() => {
    if (phase !== "exiting") return;
    const timer = setTimeout(() => {
      setPhase("done");
      onComplete();
    }, 300);
    return () => clearTimeout(timer);
  }, [phase, onComplete]);

  // skip
  const handleSkip = useCallback(() => {
    if (phase === "entering" || phase === "holding") setPhase("exiting");
  }, [phase]);

  /*
    Entrée seulement. L'espace passait ici en premier, et c'est la barre que
    `KeyboardController` a fait pédale forte : le geste par lequel on demande
    au conseil de rester à l'écran le faisait disparaître. Il n'est plus lu du
    tout — le clavier de l'ordinateur est un clavier de piano tant que le
    studio est ouvert.
  */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code === "Enter") {
        e.preventDefault();
        handleSkip();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleSkip]);

  if (phase === "done") return null;

  const isExiting = phase === "exiting";
  const isHolding = phase === "holding" && !paused;

  return (
    <div
      /* Filet de sécurité : le rideau défile plutôt que de rogner. Avec
         `items-center` et aucun `overflow`, un bloc plus haut que le calque
         débordait des deux côtés à la fois et l'ancêtre `overflow-hidden` en
         mangeait le haut (le compteur de sections) comme le bas (la barre de
         progression et sa mention), sans aucun moyen d'y accéder. Le centrage
         passe donc par un `m-auto` sur le contenu : contrairement à `align-items: center`, les
         marges auto s'effacent dès que ça déborde et laissent tout le bloc
         atteignable au défilement.

         Le calque reste plein cadre — il couvre le clavier et le rail de
         minimap — parce que c'est un rideau : sous 340 px de haut, la bande du
         clavier est précisément la hauteur qui manque. */
      className="absolute inset-0 z-20 flex overflow-y-auto overscroll-contain bg-[rgba(8,7,18,0.82)] backdrop-blur-[12px] cursor-pointer"
      style={{
        animation: isExiting
          ? "si-fade-out 250ms ease-out forwards"
          : "si-fade-in 400ms cubic-bezier(0.16, 1, 0.3, 1) forwards",
      }}
      onClick={handleSkip}
      role="dialog"
      aria-label={`Section ${sectionIndex + 1}: ${sectionLabel}`}
    >
      <div
        className="m-auto flex flex-col items-center text-center max-w-[480px] px-6 py-6 [@media(max-height:480px)]:py-3 cursor-default"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Section progress stepper */}
        <div
          className="flex items-center gap-1 mb-6 [@media(max-height:480px)]:mb-2.5 opacity-0"
          style={stagger(120)}
        >
          <span className="text-sm font-bold tabular-nums text-accent-bright">{sectionIndex + 1}</span>
          <span className="text-xs text-white/20 mx-px">/</span>
          <span className="text-sm font-medium tabular-nums text-white/35">{totalSections}</span>
          <div className="flex gap-[3px] ml-3 [@media(max-height:480px)]:ml-2">
            {Array.from({ length: totalSections }, (_, i) => (
              <span
                key={i}
                className={`w-[18px] [@media(max-height:480px)]:w-3 h-[3px] rounded-sm transition-colors duration-300 ${
                  i < sectionIndex
                    ? "bg-accent/45"
                    : i === sectionIndex
                      ? "bg-accent-bright shadow-[0_0_8px_rgba(139,92,246,0.4)]"
                      : "bg-white/[0.08]"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Section title */}
        <h2
          className="text-[32px] [@media(max-height:480px)]:text-[22px] font-bold tracking-tight leading-[1.15] text-white/[0.97] mb-5 [@media(max-height:480px)]:mb-2 opacity-0"
          style={stagger(180)}
        >
          {sectionLabel}
        </h2>

        {/* Explication — certaines sections d'exercice n'en portent pas, et un
            paragraphe vide gardait quand même sa gouttière de 24 px. */}
        {explain && (
          <p
            className="text-base [@media(max-height:480px)]:text-[13px] leading-[1.7] [@media(max-height:480px)]:leading-[1.5] text-white/80 mb-6 [@media(max-height:480px)]:mb-2.5 max-w-[440px] opacity-0"
            style={stagger(240)}
          >
            {explain}
          </p>
        )}

        {/* Le séparateur n'est là que pour détacher la consigne de
            l'explication : sans consigne, il ne sépare plus rien. */}
        {tip && (
          <>
            <div
              className="w-10 h-px bg-accent/30 mb-6 [@media(max-height:480px)]:mb-2.5 opacity-0"
              style={stagger(300)}
            />

            <p
              className="text-[17px] [@media(max-height:480px)]:text-[14px] font-medium leading-relaxed text-white/95 mb-9 [@media(max-height:480px)]:mb-3 max-w-[440px] opacity-0"
              style={stagger(360)}
            >
              {tip}
            </p>
          </>
        )}

        {/* Progress + hint */}
        <div
          className="flex flex-col items-center gap-2.5 [@media(max-height:480px)]:gap-1.5 w-[200px] opacity-0"
          style={stagger(420)}
        >
          <div className="w-full h-0.5 bg-white/[0.06] rounded-full overflow-hidden" role="progressbar">
            <div
              className="h-full rounded-full"
              style={{
                background: "linear-gradient(to right, rgba(139, 92, 246, 0.3), rgba(167, 139, 250, 0.8))",
                boxShadow: "0 0 8px rgba(139, 92, 246, 0.25)",
                transition: isHolding ? `width ${holdRemainingRef.current}ms linear` : "none",
                width: isHolding ? "100%" : `${frozenPct}%`,
              }}
            />
          </div>
          {/* La ligne dit ce qui est en train de se passer : le minuteur
              court, ou il attend qu'on relâche la pédale. */}
          {holdPaused ? (
            <span className="text-xs text-accent-bright/80">
              Relâche la pédale pour reprendre
            </span>
          ) : (
            <span className="text-xs text-white/45 flex items-center gap-1">
              <kbd className="text-[11px] font-[inherit] px-1.5 py-0.5 rounded border border-white/[0.18] bg-white/[0.06] text-white/55">
                Entrée
              </kbd>
              {" "}pour continuer
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
